"use client";

import Image from "next/image";
import React from "react";
import eventosYAcciones, { Evento, EventoImagen } from "./event";
import {
	Collapsible,
	CollapsibleTrigger,
	CollapsibleContent,
} from "@/components/ui/Collapsible";
import ReactMarkdown from "react-markdown";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";
import dayjs from "dayjs";
import {
	IconCalendar,
	IconPhoto,
	IconVideo,
	IconChevronDown,
} from "@/lib/tabler-icons";
import Link from "next/link";
import { SectionHeading, Kicker } from "@/components/ui/editorial";

/**
 * Badge de fecha editorial
 */
const DateBadge: React.FC<{ date: string }> = ({ date }) => (
	<div className="inline-flex items-center gap-2 border border-border text-ink px-4 py-1.5 rounded-full text-sm font-body">
		<IconCalendar className="w-4 h-4 text-terracotta" />
		<time dateTime={date} className="font-medium">
			{dayjs(date).format("DD MMM YYYY")}
		</time>
	</div>
);

/**
 * Galería de imágenes editorial
 */
const ImageGallery: React.FC<{ images: EventoImagen[] }> = ({ images }) => {
	const validImages = images.filter((image) => !!image.url);

	if (validImages.length === 0) return null;

	return (
		<div className="space-y-4">
			<div className="flex items-center gap-2">
				<IconPhoto className="w-4 h-4 text-terracotta" />
				<h4 className="font-display text-lg font-semibold text-ink">Galería de Fotos</h4>
			</div>
			<div className="grid grid-cols-2 md:grid-cols-3 gap-4">
				{validImages.map((image, idx) => (
					<Zoom key={image.id}>
						<div className="relative aspect-square rounded-xl overflow-hidden border border-border group">
							<Image
								src={image.url.replace(/^\./, "")}
								alt={image.alt || `Imagen ${idx + 1} del evento`}
								title={image.title}
								fill
								sizes="(max-width: 768px) 50vw, 33vw"
								className="object-cover group-hover:scale-105 transition-transform duration-500"
								loading="lazy"
								placeholder="blur"
								blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
							/>
							{image.title && (
								<div className="absolute inset-0 bg-ink/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
									<p className="text-cream text-sm font-body p-3">
										{image.title}
									</p>
								</div>
							)}
						</div>
					</Zoom>
				))}
			</div>
		</div>
	);
};

/**
 * Video editorial
 */
const EventVideo: React.FC<{ url: string; title?: string }> = ({
	url,
	title,
}) => {
	// Detectar si es un video de YouTube
	const isYouTubeVideo =
		url.includes("youtube.com/embed") || url.includes("youtu.be");

	if (isYouTubeVideo) {
		return (
			<div className="space-y-4">
				<div className="flex items-center gap-2">
					<IconVideo className="w-4 h-4 text-terracotta" />
					<h4 className="font-display text-lg font-semibold text-ink">Video del Evento</h4>
				</div>
				<div className="relative aspect-video rounded-xl overflow-hidden border border-border bg-ink/5">
					<iframe
						src={url}
						title={title || "Video del evento"}
						className="w-full h-full"
						frameBorder="0"
						allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
						allowFullScreen
						loading="lazy"
					/>
				</div>
			</div>
		);
	}

	// Para videos locales
	return (
		<div className="space-y-4">
			<div className="flex items-center gap-2">
				<IconVideo className="w-4 h-4 text-terracotta" />
				<h4 className="font-display text-lg font-semibold text-ink">Video del Evento</h4>
			</div>
			<div className="relative aspect-video rounded-xl overflow-hidden border border-border bg-ink/5">
				<video
					controls
					preload="none"
					poster="/assets/logo-vector.svg"
					className="w-full h-full object-cover"
					aria-label={title || "Video del evento"}
				>
					<source src={url} type="video/mp4" />
					Tu navegador no soporta la reproducción de video.
				</video>
			</div>
		</div>
	);
};

/**
 * Sección de eventos con diseño editorial
 */
const eventosOrdenados = [...eventosYAcciones].sort(
	(a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf()
);

const EventsSection: React.FC = () => {
	return (
		<section
			id="eventos"
			className="px-4 xl:px-0 py-16"
			aria-labelledby="eventos-heading"
			role="region"
		>
			<div className="container max-w-7xl mx-auto space-y-12">
				<header>
					<SectionHeading
						index="02"
						title="Eventos y Acciones"
						kicker="Actividades Recientes"
					/>
					<p
						id="eventos-heading"
						className="font-body text-lg mt-4 max-w-3xl leading-relaxed text-muted-foreground"
					>
						Descubre nuestras actividades y logros recientes en la comunidad.
						Cada evento representa un paso más hacia la transformación social.
					</p>
				</header>

				<div className="grid gap-8 md:gap-12">
					{eventosOrdenados.map((evento: Evento) => (
						<article
							key={evento.id}
							className="border border-border rounded-xl overflow-hidden"
							role="article"
							aria-labelledby={`evento-${evento.id}-title`}
						>
							{/* Header del evento */}
							<div className="p-6 md:p-8 border-b border-border">
								<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
									<div className="flex-1">
										<div className="flex flex-wrap items-center gap-3 mb-3">
											<DateBadge date={evento.date} />
											{evento.images && evento.images.length > 0 && (
												<Kicker>
													<span className="inline-flex items-center gap-1.5">
														<IconPhoto className="w-3 h-3" />
														{evento.images.length} foto
														{evento.images.length !== 1 ? "s" : ""}
													</span>
												</Kicker>
											)}
											{evento.video && (
												<Kicker>
													<span className="inline-flex items-center gap-1.5">
														<IconVideo className="w-3 h-3" />
														Video
													</span>
												</Kicker>
											)}
										</div>
										<h3
											id={`evento-${evento.id}-title`}
											className="font-display text-2xl md:text-3xl font-semibold text-ink leading-tight"
										>
											{evento.title}
										</h3>
									</div>
								</div>
							</div>

							{/* Contenido del evento */}
							<Collapsible className="w-full group/collapsible">
								<CollapsibleTrigger className="flex w-full items-center justify-between gap-4 px-6 md:px-8 py-5 hover:bg-ink/[0.03] transition-colors duration-200 cursor-pointer border-t border-border/50">
									<span className="font-body text-terracotta font-medium text-sm tracking-wide uppercase">
										Ver detalles del evento
									</span>
									<IconChevronDown className="w-5 h-5 text-terracotta transition-transform duration-300 group-data-[state=open]/collapsible:rotate-180" />
								</CollapsibleTrigger>
								<CollapsibleContent className="px-6 md:px-8 pb-8">
									<div className="space-y-8 pt-4">
										<div className="font-body text-ink/80 text-base leading-relaxed">
											<ReactMarkdown
												components={{
													p: ({ children }) => (
														<p className="mb-4 last:mb-0 text-ink/80 leading-relaxed font-body">
															{children}
														</p>
													),
													strong: ({ children }) => (
														<strong className="font-semibold text-ink">
															{children}
														</strong>
													),
													a: ({ href, children }) => (
														<Link
															href={href || "/"}
															className="text-terracotta hover:text-terracotta/80 underline underline-offset-2 transition-colors duration-200 font-medium"
															target="_blank"
															rel="noopener noreferrer"
														>
															{children}
														</Link>
													),
												}}
											>
												{evento.description}
											</ReactMarkdown>
										</div>

										{evento.images && evento.images.length > 0 && (
											<ImageGallery images={evento.images} />
										)}

										{evento.video && (
											<EventVideo url={evento.video} title={evento.title} />
										)}
									</div>
								</CollapsibleContent>
							</Collapsible>
						</article>
					))}
				</div>

				{/* Footer de la sección */}
				<div className="pt-8 text-center border-t border-border">
					<p className="font-display text-2xl md:text-3xl font-semibold text-ink mb-4">
						¿Te gustaría participar?
					</p>
					<p className="font-body text-muted-foreground text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
						Únete a nuestra comunidad y participa en los próximos eventos.
						Tus ideas y energía son fundamentales para crear un futuro mejor
						para todos.
					</p>
					<Link
						href="#contacto"
						className="font-body inline-block bg-ink text-cream px-8 py-3 rounded-full font-medium hover:bg-ink/90 transition-colors duration-200"
					>
						Contactar
					</Link>
				</div>
			</div>
		</section>
	);
};

export default EventsSection;
